/**
 * Utility functions for deal-related operations
 */

/**
 * Capitalizes the first letter of a string
 * @param {string} str - The string to capitalize
 * @returns {string} - Capitalized string
 */
export const capitalizeFirstLetter = (str) => {
  return str ? str.charAt(0).toUpperCase() + str.slice(1) : '';
};

/**
 * Builds the app-relative path to a deal's details page
 * @param {Object} deal - The deal object
 * @returns {string} - Path like "/deals/detail/123/overview"
 */
export const getDealDetailsPath = deal => `/deals/detail/${deal.id}/overview`;

/**
 * Builds the fully-qualified, shareable URL to a deal's details page
 * @param {Object} deal - The deal object
 * @returns {string} - Absolute URL including origin
 */
export const getDealDetailsUrl = deal => `${window.location.origin}${getDealDetailsPath(deal)}`;

/**
 * Formats deal type and rep role into a display string
 * @param {string} dealType - The deal type
 * @param {string} repRole - The rep role
 * @returns {string} - Formatted string like "Sale - Tenant Rep" or "--"
 */
export const formatDealTypeAndRole = (dealType, repRole) => {
  const type = capitalizeFirstLetter(dealType);
  const role = capitalizeFirstLetter(repRole);
  return [type, role].filter(Boolean).join(' - ') || '--';
};

/**
 * Finds the deal ID from linked MTA transactions
 * @param {Array} linkedMtaTransactions - Array of linked MTA transactions
 * @param {string} selectedTransactionId - The selected transaction ID
 * @returns {string} - The corresponding deal ID or the transaction ID as fallback
 */
export const findLinkedDealId = (linkedMtaTransactions, selectedTransactionId) => {
  const linkedTransaction = linkedMtaTransactions?.find(
    tx => tx.transaction_id === selectedTransactionId
  );
  return linkedTransaction?.deal_id || selectedTransactionId;
};

/**
 * Formats deal stage with proper capitalization
 * @param {string} dealStage - The deal stage
 * @returns {string} - Formatted deal stage or "--"
 */
export const formatDealStage = (dealStage) => {
  return dealStage ? dealStage.charAt(0).toUpperCase() + dealStage.slice(1) : '--';
};

/**
 * Renders voucher submissions content based on the voucher array
 * @param {Array} voucherSubmissions - Array of voucher submissions
 * @returns {Object} - Object with hasVouchers flag and vouchers array
 */
export const prepareVoucherData = (voucherSubmissions) => {
  const vouchers = Array.isArray(voucherSubmissions) ? voucherSubmissions : [];
  return {
    hasVouchers: vouchers.length > 0,
    vouchers: vouchers
  };
};

/**
 * Prepares commission users data for rendering
 * @param {Array} uniqueCommissionUsers - Array of unique commission users
 * @returns {Object} - Object with hasUsers flag and users array
 */
export const prepareCommissionUsersData = (uniqueCommissionUsers) => {
  return {
    hasUsers: uniqueCommissionUsers && uniqueCommissionUsers.length > 0,
    users: uniqueCommissionUsers || []
  };
};

/**
 * Utility function to safely render user info
 * @param {Object|string} user - The user object or string
 * @returns {string} - Formatted user name
 */
export const getUserName = (user) => {
  if (!user) return '';
  if (typeof user === 'string') return user;
  if (user.first_name || user.last_name) return `${user.first_name || ''} ${user.last_name || ''}`.trim();
  if (user.username) return user.username;
  return 'Unknown User';
};

/**
 * Helper to get lead producer name
 * @param {Object} leadProducer - The lead producer object
 * @returns {string} - Formatted lead producer name
 */
export const getLeadProducerName = (leadProducer) => {
  if (leadProducer && leadProducer.broker) return getUserName(leadProducer.broker);
  if (leadProducer && leadProducer.outside_broker_name) return leadProducer.outside_broker_name;
  return '--';
};
